import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  Icon,
  Avatar,
  Image,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { ChatIcon, CopyIcon, CheckIcon } from '@chakra-ui/icons';
import { FaRegCopy, FaEdit, FaDownload, FaFilePdf, FaFileWord, FaFileExcel, FaFileCsv, FaFile } from 'react-icons/fa';
import { BiRefresh } from 'react-icons/bi';
import MarkdownRenderer from './MarkdownRenderer';
import { useAuth } from '../contexts/AuthContext';

// Function to get document icon based on file extension
const getDocumentIcon = (filename) => {
  const extension = filename.split('.').pop().toLowerCase();
  switch (extension) {
    case 'pdf':
      return FaFilePdf;
    case 'doc':
    case 'docx':
      return FaFileWord;
    case 'xls':
    case 'xlsx':
      return FaFileExcel;
    case 'csv':
      return FaFileCsv;
    default:
      return FaFile;
  }
};

// Component for message avatar and name
const MessageAvatar = ({ isUser, assistantName = "Assistant" }) => {
  const { user } = useAuth();
  const assistantFirstLetter = assistantName?.charAt(0) || 'A';
  const userFirstLetter = user?.username?.charAt(0) || 'U';

  return (
    <Flex alignItems="center">
      <Avatar
        size="sm"
        name={isUser ? userFirstLetter : assistantFirstLetter}
        bg="#e2e8f0"
        color="#1f2937"
        icon={!isUser && <ChatIcon fontSize="0.8rem" />}
      />
      <Text fontWeight="bold" fontSize="md" ml={3}>
        {isUser ? user?.username || 'User' : assistantName}
      </Text>
    </Flex>
  );
};

// Component for message action buttons
const MessageActions = ({ 
  isUser, 
  hasImage, 
  onCopy, 
  onEdit, 
  onRegenerate, 
  onDownload, 
  messageContent, 
  isLastMessage, 
  isLastResponse,
  hasDocumentsInLastMessage
}) => {
  const [justCopied, setJustCopied] = useState(false);

  const handleCopy = () => {
    let textToCopy = '';
    
    // Handle array content (for messages with images)
    if (Array.isArray(messageContent)) {
      textToCopy = messageContent
        .filter(item => item.type === 'text' || item.type === 'input_text')
        .map(item => item.text)
        .join('\n');
    } else if (typeof messageContent === 'string') {
      const docMatch = messageContent.match(/\[DOCUMENT_REFERENCES:{.*?}\]/);
      textToCopy = docMatch ? messageContent.replace(docMatch[0], '').trim() : messageContent;
    }

    const textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      setJustCopied(true);
      setTimeout(() => {
        setJustCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
    document.body.removeChild(textArea);
    onCopy();
  };

  // Don't show edit/regenerate buttons if last message has documents
  const showEditButton = isUser && isLastMessage && !hasDocumentsInLastMessage;
  const showRegenerateButton = !isUser && isLastResponse && !hasDocumentsInLastMessage;

  return (
    <Flex 
      gap={1} 
      alignItems="center" 
      opacity={isUser ? 0 : 1}
      _groupHover={{ opacity: 1 }}
    >
      {/* Only show download button for AI-generated images */}
      {hasImage && !isUser && (
        <IconButton
          icon={<Icon as={FaDownload} boxSize="16px" />}
          variant="ghost"
          color="#64748b"
          size="sm"
          p={1}
          minW="auto"
          h="auto"
          onClick={onDownload}
          _hover={{ color: '#1f2937' }}
          aria-label="Download"
        />
      )}
      
      {/* Copy button - always visible */}
      <IconButton
        icon={<Icon as={justCopied ? CheckIcon : FaRegCopy} boxSize="16px" />}
        variant="ghost"
        color="#64748b"
        size="sm"
        p={1}
        minW="auto"
        h="auto"
        onClick={handleCopy}
        _hover={{ color: '#1f2937' }}
        aria-label="Copy message"
      />
      
      {/* Edit button - only for last user message without documents */}
      {showEditButton && (
        <IconButton
        icon={<Icon as={FaEdit} boxSize="16px" />}
        variant="ghost"
        color="#64748b"
        size="sm"
        p={1}
        minW="auto"
        h="auto"
        onClick={onEdit}
        _hover={{ color: '#1f2937' }}
        aria-label="Edit message"
      />
      )}
      
      {/* Regenerate button - only for last assistant response without documents */}
      {showRegenerateButton && (
        <IconButton
        icon={<Icon as={BiRefresh} boxSize="20px" />}
        variant="ghost"
        color="#64748b"
        size="sm"
        p={1}
        minW="auto"
        h="auto"
        onClick={onRegenerate}
        _hover={{ color: '#1f2937' }}
        aria-label="Regenerate response"
      />
      )}
    </Flex>
  );
};

// Component for message header
const MessageHeader = ({ 
  isUser, 
  userName, 
  assistantName, 
  hasImage, 
  onCopy, 
  onEdit, 
  onRegenerate, 
  onDownload, 
  messageContent, 
  isLastMessage, 
  isLastResponse,
  hasDocumentsInLastMessage
}) => (
  <Flex alignItems="center" mb={4} justify="space-between">
    <MessageAvatar isUser={isUser} assistantName={assistantName} />
    <MessageActions 
      isUser={isUser} 
      hasImage={hasImage}
      onCopy={onCopy}
      onEdit={onEdit}
      onRegenerate={onRegenerate}
      onDownload={onDownload}
      messageContent={messageContent}
      isLastMessage={isLastMessage}
      isLastResponse={isLastResponse}
      hasDocumentsInLastMessage={hasDocumentsInLastMessage}
    />
  </Flex>
);

// Function to parse document references from message content
const parseDocumentReferences = (content) => {
    const match = content.match(/\[DOCUMENT_REFERENCES:{(.*?)}\]/);
    if (match) {
        const documentNames = match[1].split(',');
        const cleanContent = content.replace(/\[DOCUMENT_REFERENCES:{.*?}\]/, '').trim();
        return { documentNames, cleanContent };
    }
    return { documentNames: [], cleanContent: content };
};

// Component for message content
const MessageContent = ({ message, isEditing, onSave, onCancel, editValue, onEditChange, isWaitingForFirstResponse, isWaitingForEditResponse }) => {
    if (isEditing) {
        const images = Array.isArray(message.content) 
            ? message.content.filter(item => item.type === 'image_url' || item.type === 'input_image').map(item => item.type === 'image_url' ? item.image_url.url : item.image_url)
            : message.images?.map(base64Image => `data:image/jpeg;base64,${base64Image}`) || [];
            
        let textContent = '';
        if (Array.isArray(message.content)) {
            const textItems = message.content.filter(item => item.type === 'text' || item.type === 'input_text');
            textContent = textItems.map(item => item.text).join('\n');
        } else {
            const { cleanContent } = parseDocumentReferences(message.content || '');
            textContent = cleanContent;
        }


        return (
            <Box pl={10}>
                <Box 
                    position="relative" 
                    border="1px solid"
                    borderColor="#e2e8f0"
                    borderRadius="md"
                    bg="#ffffff"
                >
                    <Box 
                        as="textarea"
                        value={editValue}
                        onChange={onEditChange}
                        w="100%"
                        minH="100px"
                        p={2}
                        pb={images.length > 0 ? "60px" : "2"}
                        bg="transparent"
                        color="#1f2937"
                        border="none"
                        _focus={{
                            outline: "none"
                        }}
                        resize="vertical"
                    />
                    
                    {/* Display small unmodifiable images at bottom */}
                    {images.length > 0 && (
                        <Flex 
                            position="absolute" 
                            bottom={2} 
                            left={2} 
                            right={2}
                            gap={2} 
                            p={1}
                            borderTop="1px solid"
                            borderColor="#e2e8f0"
                            bg="#ffffff"
                        >
                            {images.map((imageUrl, index) => (
                                <Image
                                    key={index}
                                    src={imageUrl}
                                    alt="User uploaded"
                                    h="40px"
                                    w="40px"
                                    objectFit="cover"
                                    borderRadius="md"
                                />
                            ))}
                        </Flex>
                    )}
                </Box>

                <Flex mt={4} gap={2} justify="flex-end">
                    <Box
                        as="button"
                        px={4}
                        py={2}
                        bg="#3498db"
                        color="white"
                        borderRadius="md"
                        fontWeight="medium"
                        onClick={onSave}
                        disabled={editValue.trim() === ''}
                        opacity={!editValue.trim() ? 0.5 : 1}
                        _hover={{ bg: "#2e86c1" }}
                    >
                        Save & Send
                    </Box>
                    <Box
                        as="button"
                        px={4}
                        py={2}
                        bg="transparent"
                        color="#1f2937"
                        borderRadius="md"
                        fontWeight="medium"
                        onClick={onCancel}
                        _hover={{ bg: "#e2e8f0" }}
                    >
                        Cancel
                    </Box>
                </Flex>
            </Box>
        );
    }

    // Handle loading dot animation for both new responses and edits
    if ((isWaitingForFirstResponse || isWaitingForEditResponse) && message.content === '.') {
        return (
            <Box pl={10}>
                <Text fontSize="md" lineHeight="tall" color="#3498db" height="24px" display="flex" alignItems="center">
                    <span className="loading-dot">.</span>
                </Text>
                <style>{`
                    @keyframes blink {
                        0% { opacity: .2; }
                        20% { opacity: 1; }
                        100% { opacity: .2; }
                    }
                    .loading-dot {
                        animation: blink 1.4s infinite;
                        font-size: 6rem;
                        font-weight: bold;
                        line-height: 0;
                        position: relative;
                        top: -2rem;
                    }
                `}</style>
            </Box>
        );
    }

    // Handle array content (for messages with images/text)
    if (Array.isArray(message.content)) {
        return (
            <Box pl={10}>
                {message.content.map((item, index) => {
                    if (item.type === 'text' || item.type === 'input_text') {
                        return (
                            <Text key={index} fontSize="md" lineHeight="tall" color="#1f2937">
                                {item.text}
                            </Text>
                        );
                    } else if (item.type === 'image_url') {
                        return (
                            <Image
                                key={index}
                                src={item.image_url.url}
                                alt="User uploaded"
                                maxH="200px"
                                borderRadius="md"
                                mt={2}
                            />
                        );
                    } else if (item.type === 'input_image') {
                        return (
                            <Image
                                key={index}
                                src={item.image_url}
                                alt="User uploaded"
                                maxH="200px"
                                borderRadius="md"
                                mt={2}
                            />
                        );
                    }
                    return null;
                })}
            </Box>
        );
    }

    // Handle legacy format (separate content and images)
    const { documentNames: parsedDocNames, cleanContent } = parseDocumentReferences(message.content || '');
    const displayDocNames = message.document_names || parsedDocNames;

    return (
        <Box pl={10}>
            {displayDocNames.length > 0 && (
                <Flex gap={2} mb={2} flexWrap="wrap">
                    {displayDocNames.map((docName, index) => (
                        <Flex 
                            key={`doc-${index}`}
                            alignItems="center" 
                            bg="#e2e8f0" 
                            p={2} 
                            borderRadius="md"
                            mr={2}
                            mb={2}
                        >
                            <Icon 
                                as={getDocumentIcon(docName)}
                                boxSize="16px"
                                mr={2}
                                color="gray.400"
                            />
                            <Text fontSize="sm">{docName}</Text>
                        </Flex>
                    ))}
                </Flex>
            )}
            {message.images && message.images.length > 0 && (
                <Flex gap={2} mb={2} flexWrap="wrap">
                    {message.images.map((base64Image, index) => (
                        <Image
                            key={index}
                            src={`data:image/jpeg;base64,${base64Image}`}
                            alt="User uploaded"
                            maxH="200px"
                            borderRadius="md"
                        />
                    ))}
                </Flex>
            )}
            {cleanContent && (
                <MarkdownRenderer content={cleanContent} />
            )}
            {message.imageUrl && (
                <Image
                    src={message.imageUrl}
                    alt="Generated image"
                    w="full"
                    maxH="400px"
                    objectFit="contain"
                    mt={cleanContent ? 4 : 0}
                    borderRadius="md"
                />
            )}
            {message.cot && (
                <Accordion allowToggle mb={4}>
                    <AccordionItem border="none">
                        <AccordionButton px={0} _hover={{ bg: 'transparent' }}>
                            <Text fontSize="sm" color="gray.600">
                                Show Reasoning
                            </Text>
                            <AccordionIcon color="gray.600" />
                        </AccordionButton>
                        <AccordionPanel px={0} py={2}>
                            <Box bg="#f8fafc" p={4} borderRadius="md">
                                <MarkdownRenderer content={message.cot} />
                            </Box>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
            )}
        </Box>
    );
};

// Main message component
const Message = React.memo(({ 
  message, 
  userName = "Abhay",
  assistantName = "Assistant",
  onCopy,
  onEdit,
  onRegenerate,
  onDownload,
  isLastMessage = false,
  isLastResponse = false,
  hasDocumentsInLastMessage = false,
  isWaitingForFirstResponse = false,
  isWaitingForEditResponse = false
}) => {
  const isUser = message.role === 'user';
  const hasImages = message.images?.length > 0 || 
                   (Array.isArray(message.content) && 
                    message.content.some(item => item.type === 'image_url'  || item.type === 'input_image'));
  const hasImageUrl = !!message.imageUrl;
  const hasDocuments = message.document_names?.length > 0;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const handleEditStart = () => {
    let initialValue = '';
    if (typeof message.content === 'string') {
      initialValue = message.content;
    } else if (Array.isArray(message.content)) {
      const textItems = message.content.filter(item => item.type === 'text' || item.type === 'input_text');
      initialValue = textItems.map(item => item.text).join('\n');
    }
    setEditValue(initialValue);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (onEdit && editValue.trim() !== '') {
      const images = Array.isArray(message.content) 
        ? message.content.filter(item => item.type === 'image_url'  || item.type === 'input_image').map(item => item.type === 'image_url' ? item.image_url.url : item.image_url)
        : message.images || [];

      onEdit(editValue.trim());
    }
    setIsEditing(false);
    setEditValue('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  return (
    <Box w="100%" bg={isUser ? '#f8fafc' : '#ffffff'} py={6} role="group">
      <Flex maxW="4xl" w="min(90%, 62rem)" mx="auto" px={6} color="#1f2937" direction="column">
        <MessageHeader 
          isUser={isUser}
          userName={userName}
          assistantName={assistantName}
          hasImage={hasImages || hasImageUrl}
          onCopy={onCopy}
          onEdit={handleEditStart}
          onRegenerate={onRegenerate}
          onDownload={onDownload}
          messageContent={typeof message.content === 'string' ? message.content : ''}
          isLastMessage={isLastMessage}
          isLastResponse={isLastResponse}
          hasDocumentsInLastMessage={hasDocumentsInLastMessage}
        />
        <MessageContent 
          message={message} 
          isEditing={isEditing}
          onSave={handleSave}
          onCancel={handleCancel}
          editValue={editValue}
          onEditChange={(e) => setEditValue(e.target.value)}
          isWaitingForFirstResponse={isWaitingForFirstResponse}
          isWaitingForEditResponse={isWaitingForEditResponse}
        />
      </Flex>
    </Box>
  );
}, 
// Custom comparison function
(prevProps, nextProps) => {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.isLastMessage === nextProps.isLastMessage &&
    prevProps.isLastResponse === nextProps.isLastResponse &&
    prevProps.hasDocumentsInLastMessage === nextProps.hasDocumentsInLastMessage &&
    prevProps.isWaitingForFirstResponse === nextProps.isWaitingForFirstResponse &&
    prevProps.isWaitingForEditResponse === nextProps.isWaitingForEditResponse
  );
});

export default Message; 
